/**
 * SubastasPago — Resumen del borrador + selector de método (Transferencia vs Tarjeta).
 * Tarjeta: simulada por ahora (Payphone en Slice 1.5). Total = day_fee × 1.0575.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { usePublicConfig } from "@/lib/publicConfig";
import { compressImage } from "@/lib/imageCompress";
import { MultiPhotoInput } from "@/components/MultiPhotoInput";
import { CopyableField } from "@/components/CopyableField";
import { metaLine } from "@/components/AuctionCard";
import { CardPhoto, Label, money } from "@/components/auction/AuctionUI";
import { Landmark, CreditCard, AlertTriangle, Pencil, ArrowLeft, Check } from "lucide-react";
import { HitButton, HitSectionLoading, useAsyncAction, useHeavyFlow, useLoadingLevel } from "@/components/loading";

const PAYPHONE_FEE_PCT = 0.0575;
const DRAFT_TTL_MS = 48 * 3600 * 1000;

async function uploadPrivate(file, onProgress) {
  const { file: toUpload } = await compressImage(file);
  const fd = new FormData();
  fd.append("file", toUpload);
  fd.append("scope", "private");
  fd.append("kind", "transfer_receipt");
  const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000, onUploadProgress: (e) => onProgress?.(e.total ? e.loaded / e.total : 0) });
  return r.data.path;
}

function useHm(until) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(t); }, []);
  const ms = Math.max(0, until - now);
  return `${String(Math.floor(ms / 3600000)).padStart(2, "0")}:${String(Math.floor((ms % 3600000) / 60000)).padStart(2, "0")}`;
}

function Row({ label, children }) {
  return <div className="flex justify-between gap-3 py-1 text-[12px]"><span className="text-white/45">{label}</span><span className="text-white/80 text-right">{children}</span></div>;
}

function DraftSummary({ draft, fee }) {
  const plural = (n) => `${n} día${n > 1 ? "s" : ""}`;
  return (
    <div className="sx-card p-4" data-testid="pago-draft-summary">
      <div className="flex items-center gap-3.5">
        <CardPhoto path={draft.photos?.[0]} alt={draft.product_name} w={58} h={78} size={200} rotate={0} />
        <div className="flex-1 min-w-0">
          <div className="text-lg font-extrabold leading-tight" data-testid="pago-product-name">{draft.product_name}</div>
          <div className="text-xs text-white/55 mt-1">{metaLine(draft)} · {plural(draft.duration_days)}</div>
          <div className="text-xs text-white/55">Salida {money(draft.starting_price)}</div>
        </div>
        <div className="text-right flex-none"><Label>A pagar</Label><div className="sx-price text-[26px] text-[#34D399] mt-1">{money(fee)}</div></div>
      </div>
      <div className="mt-3 pt-3 border-t border-white/10">
        {(draft.set_name || draft.rarity_text) && <Row label="Set / rareza">{[draft.set_name, draft.rarity_text].filter(Boolean).join(" · ")}</Row>}
        <Row label="Plazo de pago del ganador">{plural(draft.payment_deadline_days)}</Row>
        <Row label="Ubicación">{draft.city}, {draft.province}</Row>
        <Row label="Envío">{money(draft.shipping_cost_same_province)} en {draft.province} · {money(draft.shipping_cost_other_province)} otras</Row>
        {draft.accepts_local_delivery && <Row label="Entrega personal">{draft.meetup_location}</Row>}
        <Row label="Costo por días"><b>{money(fee)}</b></Row>
        {draft.description && <p className="text-[12px] text-white/50 mt-2 whitespace-pre-line">{draft.description}</p>}
      </div>
    </div>
  );
}

function HittcgBankInfo({ bank, fee }) {
  const ready = bank && (bank.bank_name || "").trim() && (bank.account_number || "").trim();
  if (!ready) return <div className="text-sm text-[#ff8a80] bg-[#FF4438]/10 border border-[#FF4438]/40 rounded-[14px] p-3">La cuenta de HitTCG aún no está configurada. Contáctanos antes de transferir.</div>;
  const copy = async (text, msg) => {
    try { await navigator.clipboard.writeText(text); toast.success(msg); } catch { toast.error("No se pudo copiar"); }
  };
  const all = [`Banco: ${bank.bank_name}`, `Tipo: ${bank.account_type}`, `N° cuenta: ${bank.account_number}`,
    `Titular: ${bank.holder_name}`, `RUC / Cédula: ${bank.holder_id}`, `Monto: ${money(fee)}`].join("\n");
  return (
    <div className="space-y-3" data-testid="pago-hittcg-bank">
      <div className="rounded-[20px] border border-[#34D399]/40 p-4 flex items-end justify-between" style={{ background: "linear-gradient(160deg, rgba(52,211,153,.14), #0c100e)" }} data-testid="pago-bank-amount">
        <div><Label>Monto exacto</Label><div className="sx-price text-[40px] text-[#34D399] mt-1.5">{money(fee)}</div></div>
        <button type="button" onClick={() => copy(money(fee), "Monto exacto copiado")} data-testid="pago-bank-amount-copy" className="sx-mono text-[10px] tracking-[.1em] text-[#34D399] border border-[#34D399]/45 rounded-full px-3.5 py-2">COPIAR</button>
      </div>
      <div className="sx-card overflow-hidden [&>div]:rounded-none [&>div]:border-0 [&>div]:border-b [&>div]:border-white/10 [&>div]:bg-transparent [&>div]:px-4 [&>div]:py-3">
        <CopyableField label="Banco" value={bank.bank_name} testid="pago-bank-name" />
        <CopyableField label="Tipo" value={(bank.account_type || "").toUpperCase()} testid="pago-bank-type" />
        <CopyableField label="N° cuenta" value={bank.account_number} testid="pago-bank-account" />
        <CopyableField label="Titular" value={bank.holder_name} testid="pago-bank-holder" />
        <CopyableField label="RUC / Cédula titular" value={bank.holder_id} testid="pago-bank-id" />
        <div className="!border-b-0 !bg-amber-500/[.08] text-[13px] text-amber-300" data-testid="pago-transfer-motivo">Coloca en el motivo los días que pagaste por ejemplo "Subasta 3 días"</div>
      </div>
      <button type="button" onClick={() => copy(all, "Datos bancarios copiados")} data-testid="pago-bank-copy-all"
              className="w-full h-[54px] rounded-full border border-[#FF4438]/60 text-white font-extrabold hover:bg-[#FF4438]/10 transition-colors">Copiar todos los datos</button>
    </div>
  );
}

function MethodCard({ on, onClick, icon: Icon, title, sub, amount, note, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
            className={`relative text-left rounded-[20px] border p-4 transition-colors ${on ? "border-[#FF4438]/80 bg-gradient-to-b from-[#FF4438]/[.14] to-[#101010]" : "border-white/12 bg-[#101010] hover:border-white/30"}`}>
      <span className={`absolute top-3.5 right-3.5 w-6 h-6 rounded-full flex items-center justify-center ${on ? "bg-[#FF4438] text-[#080808]" : "border border-white/25"}`}>{on && <Check size={14} strokeWidth={3} />}</span>
      <Icon size={22} className={on ? "text-[#ff8a80]" : "text-white/60"} />
      <div className="text-[17px] font-extrabold mt-3">{title}</div>
      <div className="text-xs text-white/55">{sub}</div>
      <div className={`sx-price text-[22px] mt-3 ${on ? "text-[#34D399]" : "text-white"}`}>{amount}</div>
      {note && <div className="text-[10px] text-amber-300/80 mt-2 leading-snug">{note}</div>}
    </button>
  );
}

export default function SubastasPago() {
  const { draft_token } = useParams();
  const navigate = useNavigate();
  const config = usePublicConfig();
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pick, setPick] = useState("transfer");
  const [method, setMethod] = useState(null);
  const [phase, setPhase] = useState("");
  const [proofPhotos, setProofPhotos] = useState([]);
  const [bankRef, setBankRef] = useState("");
  const [expiredError, setExpiredError] = useState(false);
  const expires = draft ? new Date(draft.created_at).getTime() + DRAFT_TTL_MS : 0;
  const left = useHm(expires);
  const level = useLoadingLevel(loading);

  useEffect(() => {
    api.get(`/auctions/drafts/${draft_token}`)
      .then((r) => setDraft(r.data))
      .catch((err) => {
        if (err?.response?.status === 404) setExpiredError(true);
        else toast.error(err?.response?.data?.detail || "No pudimos cargar el borrador");
      })
      .finally(() => setLoading(false));
  }, [draft_token]);

  const payAct = useAsyncAction(async () => {
    try {
      let body = { payment_method: "card" };
      if (method === "transfer") {
        setPhase("upload");
        setFlow({ step: 0, progress: 0 });
        const path = await uploadPrivate(proofPhotos[0], (f) => setFlow({ step: 0, progress: f * 0.6 }));
        body = { payment_method: "transfer", transfer_proof_path: path, bank_reference: bankRef.trim() };
      }
      setPhase("pay");
      setFlow((s) => ({ step: 1, progress: s.progress == null ? null : 0.75 }));
      const r = await api.post(`/auctions/drafts/${draft_token}/confirm`, body);
      setFlow((s) => ({ step: 3, progress: s.progress == null ? null : 1 }));
      if (r.data.checkout_url) { window.location.href = r.data.checkout_url; await new Promise(() => {}); }
      return () => { toast.success(r.data.message || "Pago registrado"); navigate("/subastas/mis-subastas"); };
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.error("El borrador expiró. Vuelve a crear la subasta.");
        navigate("/subastas/crear");
        return false;
      }
      toast.error(err?.response?.data?.detail || "No se pudo registrar el pago");
      return false;
    }
  });
  const setFlow = useHeavyFlow(payAct.loading, "payment");
  const paying = payAct.loading;
  const pay = () => {
    if (method === "transfer") {
      if (!proofPhotos[0]) { toast.error("Sube la foto del comprobante"); return; }
      if (!bankRef.trim()) { toast.error("Ingresa el N° de comprobante"); return; }
    }
    payAct.run();
  };

  if (loading || level !== "none") return <div className="sx max-w-xl mx-auto px-5 py-8 min-h-[70vh]"><HitSectionLoading level={level} loading={loading} section="auction" /></div>;
  if (expiredError || !draft) return (
    <div className="sx max-w-xl mx-auto px-5 py-12">
      <div className="sx-card border-amber-500/40 p-6 text-center">
        <AlertTriangle className="text-amber-400 mx-auto mb-3" size={32} />
        <h2 className="sx-h text-2xl mb-2">Borrador <span className="sx-it">no disponible</span></h2>
        <p className="text-sm text-white/60 mb-5">El borrador no se encuentra. Los borradores sin pagar se borran a las 48 horas.</p>
        <button onClick={() => navigate("/subastas/crear")} className="sx-btn w-full h-[52px]">Crear subasta</button>
      </div>
    </div>
  );

  const fee = Number(draft.day_fee_owed || 0);
  const totalCard = fee * (1 + PAYPHONE_FEE_PCT);
  const back = (
    <button type="button" onClick={() => setMethod(null)} data-testid="pago-back-method" className="text-sm text-white/65 hover:text-white flex items-center gap-1"><ArrowLeft size={14} /> Otro método</button>
  );

  return (
    <div className="sx relative overflow-hidden">
      <div className="sx-halo" />
      <div className="relative max-w-xl mx-auto px-5 pt-7 pb-16 space-y-4">
        {!method && (
          <>
            <div className="sx-mono text-[10px] tracking-[.16em] text-[#ff8a80]">TU SUBASTA · BORRADOR</div>
            <h1 className="sx-h text-[38px]">Pagar días <span className="sx-it">adicionales</span></h1>
            <DraftSummary draft={draft} fee={fee} />
            <div className="rounded-[18px] border border-amber-500/45 bg-amber-500/[.07] p-4 flex items-center gap-4" data-testid="pago-expires-notice">
              <div className="text-center flex-none pr-4 border-r border-amber-500/30">
                <div className="sx-mono text-[24px] text-amber-400 leading-none" data-testid="pago-expires-countdown">{left}</div>
                <div className="sx-mono text-[9px] tracking-[.14em] text-amber-400/70 mt-1">H : MIN</div>
              </div>
              <p className="text-[13px] text-white/75 leading-relaxed">Completa el pago antes de <b className="text-amber-300">{new Date(expires).toLocaleString("es-EC", { dateStyle: "short", timeStyle: "short" })}</b>. Si no, este borrador se borra automáticamente (48 horas).</p>
            </div>
            <p className="text-[13px] text-white/60 leading-relaxed">El costo de los días se cobra por tener la subasta activa ese tiempo, <b className="text-white">se venda o no</b>. HitTCG no cobra comisión al cerrar la subasta.</p>
            <Label className="pt-1">Elige cómo pagar</Label>
            <div className="grid grid-cols-2 gap-2.5">
              <MethodCard on={pick === "transfer"} onClick={() => setPick("transfer")} icon={Landmark} title="Transferencia" sub="Sin recargo" amount={money(fee)} testid="pago-method-transfer-card" />
              <MethodCard on={pick === "card"} onClick={() => setPick("card")} icon={CreditCard} title="Tarjeta" sub="+5.75% Payphone" amount={money(totalCard)}
                          note="Integración Payphone en progreso — por ahora el cobro se simula." testid="pago-method-card-card" />
            </div>
            <p className="text-[12px] text-white/45">{pick === "transfer" ? "Transfieres a HitTCG, subes el comprobante y el admin lo verifica." : "Incluye 5.75% de comisión Payphone."}</p>
            <button type="button" onClick={() => setMethod(pick)} data-testid={`pago-method-${pick}`} className="sx-btn w-full h-[56px] text-base">Continuar con {pick === "transfer" ? "transferencia" : "tarjeta"}</button>
            <button type="button" onClick={() => navigate(`/subastas/crear?draft=${draft_token}`)} data-testid="pago-edit-draft" className="sx-btn-ghost w-full h-[52px] flex items-center justify-center gap-2"><Pencil size={14} /> Regresar y editar subasta</button>
          </>
        )}

        {method === "transfer" && (
          <>
            <div className="flex items-center justify-between"><span className="sx-mono text-[10px] tracking-[.14em] text-[#FF4438]">1 · TRANSFIERE A HITTCG</span>{back}</div>
            <HittcgBankInfo bank={config.hittcg_bank_info} fee={fee} />
            <div className="sx-mono text-[10px] tracking-[.14em] text-[#FF4438] pt-3">2 · SUBE EL COMPROBANTE</div>
            <MultiPhotoInput photos={proofPhotos} onChange={setProofPhotos} min={1} max={1} testid="pago-proof" hint="Foto o captura del comprobante de transferencia." />
            <div>
              <div className="text-[13px] font-bold text-white/85 mb-2">N° de comprobante <span className="text-[#FF4438]">*</span></div>
              <input value={bankRef} onChange={(e) => setBankRef(e.target.value)} placeholder="Ej: 12345678" data-testid="pago-bank-ref" className="sx-input sx-mono w-full h-[52px] px-4 text-[15px]" />
            </div>
            <HitButton onClick={pay} loading={paying} done={payAct.done} loadingText={phase === "upload" ? "Subiendo comprobante…" : "Procesando pago…"} doneText="Listo" tone="dark" data-testid="pago-pay-btn" className="sx-btn w-full h-[56px] text-base inline-flex items-center justify-center gap-2.5">
              {`Enviar ${money(fee)} a revisión`}
            </HitButton>
          </>
        )}

        {method === "card" && (
          <>
            <div className="flex items-center justify-between"><span className="sx-mono text-[10px] tracking-[.14em] text-[#FF4438]">PAGO CON TARJETA · SIMULADO</span>{back}</div>
            <div className="sx-card p-5">
              <div className="flex justify-between text-[15px] text-white/80"><span>Días adicionales</span><span className="sx-mono">{money(fee)}</span></div>
              <div className="flex justify-between text-[15px] text-white/80 mt-2"><span>Recargo Payphone · 5.75%</span><span className="sx-mono">{money(totalCard - fee)}</span></div>
              <div className="flex items-end justify-between mt-4 pt-4 border-t border-dashed border-white/20">
                <Label>Total a pagar</Label><span className="sx-price text-[40px] text-[#34D399]" data-testid="pago-card-total">{money(totalCard)}</span>
              </div>
            </div>
            <div className="rounded-[18px] border border-amber-500/45 bg-amber-500/[.07] p-4 text-[13px] text-amber-300 leading-relaxed">Pronto se reemplaza por el cobro real de Payphone. Por ahora al confirmar se registra el pago como exitoso.</div>
            <HitButton onClick={pay} loading={paying} done={payAct.done} loadingText="Procesando pago…" doneText="Listo" tone="dark" data-testid="pago-pay-btn" className="sx-btn w-full h-[56px] text-base inline-flex items-center justify-center gap-2.5">
              {`Pagar ${money(totalCard)} (simulado)`}
            </HitButton>
            <button type="button" onClick={() => setMethod(null)} data-testid="pago-back-method-bottom" className="sx-btn-ghost w-full h-[52px] flex items-center justify-center gap-2"><ArrowLeft size={14} /> Elegir otro método de pago</button>
          </>
        )}
      </div>
    </div>
  );
}
